<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    isAvailableHere,
    TScheduleStatus,
  } from '@/domain/catalog/availability';
  import { hiddenInBranch } from '@/domain/catalog/catalog-filters';
  import { IShownSection, rowView } from '@/domain/catalog/catalog-view';
  import { IItem } from '@/interfaces/IItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2CatalogCategoryHeader from './V2CatalogCategoryHeader.vue';
  import V2CatalogProductRow from './V2CatalogProductRow.vue';
  import V2CatalogQuickAdd from './V2CatalogQuickAdd.vue';

  /**
   * One category of the list (plan §5.8, DESIGN §3.5): header, the POS-only
   * or hidden note, its product rows and «+ Agregar producto a …». What
   * changes often (selection, uploads, schedules, the editor) arrives as
   * this card's own slices, so a change elsewhere never re-renders it; rows
   * get frozen views and scalar props.
   */
  const props = withDefaults(
    defineProps<{
      section: IShownSection;
      /** Products of this category that cannot be ordered here. */
      unavailableCount: number;
      selected: Readonly<Record<number, true>>;
      /** Rows the assistant points at, for a moment. */
      highlighted: Readonly<Record<number, true>>;
      photoBusy: Readonly<Record<number, true>>;
      statuses: Readonly<Record<number, TScheduleStatus | null>>;
      categoryStatus?: TScheduleStatus | null;
      /** The product of this category open in the editor. */
      editingItemId?: number | null;
      /** This category is open in the editor. */
      editingCategory?: boolean;
    }>(),
    { categoryStatus: null, editingItemId: null, editingCategory: false }
  );

  const emit = defineEmits<{
    (e: 'photo', itemId: number): void;
  }>();

  const {
    state,
    layout,
    data,
    access,
    online,
    url,
    collapse,
    actions,
  } = useCatalogPage();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const noteAction = ref<InstanceType<typeof PosUiButton> | null>(null);

  const category = computed(() => props.section.category);
  const branchId = computed(() => data.branchId.value);
  const branchName = computed(() => restaurantStore.selectedBranch?.name ?? '');
  const available = computed(() =>
    isAvailableHere(category.value, branchId.value)
  );
  const hiddenHere = computed(() =>
    hiddenInBranch(category.value, branchId.value)
  );
  const collapsed = computed(() => collapse.isCollapsed(category.value.id));
  const wide = computed(() => layout.rowMode.value === 'wide');
  const selecting = computed(() => state.mode === 'select');
  const docked = computed(() => layout.columns.value.asideDocked);
  const filtering = computed(() => data.isFiltering.value);

  const allSelected = computed(
    () =>
      props.section.items.length > 0 &&
      props.section.items.every((item) => !!props.selected[item.id])
  );

  const note = computed<{ text: string; showAction: boolean } | null>(() => {
    if (!category.value.visible)
      return { text: t('menuPage.card.hiddenNote'), showAction: true };
    if (hiddenHere.value)
      return {
        text: t('menuPage.card.hiddenHereNote', { branch: branchName.value }),
        showAction: true,
      };
    if (category.value.posOnly)
      return { text: t('menuPage.card.posOnlyNote'), showAction: false };
    return null;
  });

  const writeTitle = computed(() =>
    online.value ? undefined : t('menuPage.errors.offline')
  );

  function viewOf(item: IItem) {
    return rowView(item, category.value, data.rowContext.value);
  }

  //#region Events

  function openCategory() {
    url.openCategory(category.value.id, docked.value);
  }

  function toggleCategory(payload: {
    on: boolean;
    anchor: HTMLElement | null;
  }) {
    actions.toggleCategory(category.value, payload.on, payload.anchor);
  }

  function openMenu(anchor: HTMLElement) {
    state.panel = { kind: 'categoryMenu', targetId: category.value.id, anchor };
  }

  function showCategory() {
    const anchor = noteAction.value?.$el;
    actions.toggleCategory(
      category.value,
      true,
      anchor instanceof HTMLElement ? anchor : null
    );
  }

  function selectAll() {
    const selected = { ...state.selectedIds };
    const select = !allSelected.value;
    for (const item of props.section.items)
      if (select) selected[item.id] = true;
      else delete selected[item.id];
    state.selectedIds = selected;
  }

  function toggleSelected(item: IItem) {
    const selected = { ...state.selectedIds };
    if (selected[item.id]) delete selected[item.id];
    else selected[item.id] = true;
    state.selectedIds = selected;
  }

  function openItem(item: IItem) {
    url.openItem(item.id, docked.value);
  }

  function toggleItem(
    item: IItem,
    payload: { on: boolean; anchor: HTMLElement | null }
  ) {
    actions.toggleItem(item, payload.on, payload.anchor);
  }

  function openPrice(item: IItem, anchor: HTMLElement) {
    state.panel = { kind: 'price', targetId: item.id, anchor };
  }

  function openRowMenu(item: IItem, anchor: HTMLElement) {
    state.panel = { kind: 'rowMenu', targetId: item.id, anchor };
  }

  //#endregion
</script>

<template>
  <article class="v2-catalog-category-card">
    <V2CatalogCategoryHeader
      :category="category"
      :count="section.total"
      :match-count="filtering ? section.matchCount : null"
      :unavailable-count="unavailableCount"
      :available="available"
      :hidden-here="hiddenHere"
      :schedule="categoryStatus"
      :branch-name="branchName"
      :collapsed="collapsed"
      :collapsible="!filtering"
      :editing="editingCategory"
      :selecting="selecting"
      :all-selected="allSelected"
      :can-toggle="access.canToggle"
      :online="online"
      :toggle-blocked-title="actions.toggleBlockedTitle(category, 'category')"
      @collapse="collapse.toggle(category.id)"
      @open="openCategory"
      @toggle="toggleCategory"
      @menu="openMenu"
      @select-all="selectAll"
    />

    <template v-if="!collapsed">
      <p v-if="note" class="card-note">
        <span class="note-text">{{ note.text }}</span>
        <PosUiButton
          v-if="note.showAction && access.canToggle"
          ref="noteAction"
          class="note-action"
          color="neutral"
          variant="outline"
          :disabled="!online"
          :title="writeTitle"
          @click="showCategory"
          >{{ t('menuPage.card.show') }}</PosUiButton
        >
      </p>

      <div class="card-rows">
        <V2CatalogProductRow
          v-for="item in section.items"
          :key="item.id"
          :view="viewOf(item)"
          :schedule="statuses[item.id] || null"
          :branch-name="branchName"
          :wide="wide"
          :selecting="selecting"
          :selected="!!selected[item.id]"
          :highlighted="!!highlighted[item.id]"
          :editing="editingItemId === item.id"
          :dimmed="!available"
          :can-manage="access.canManage"
          :can-toggle="access.canToggle"
          :online="online"
          :toggle-blocked-title="actions.toggleBlockedTitle(item)"
          :photo-busy="!!photoBusy[item.id]"
          @open="openItem(item)"
          @toggle="toggleItem(item, $event)"
          @price="openPrice(item, $event)"
          @photo="emit('photo', item.id)"
          @menu="openRowMenu(item, $event)"
          @select="toggleSelected(item)"
        />
      </div>

      <p v-if="!section.total" class="card-empty">
        {{ t('menuPage.card.empty', { category: category.name }) }}
      </p>

      <template v-if="access.canManage && !selecting">
        <V2CatalogQuickAdd
          v-if="layout.tier.value !== 'phone'"
          data-tier-only="tablet desktop"
          :category="category"
          :start-open="!section.total"
          :autofocus="state.focusQuickAddCategoryId === category.id"
        />
        <button
          v-else
          type="button"
          class="card-add"
          @click="url.openNewItem(category.id)"
        >
          <i class="mdi mdi-plus add-icon" />
          <span class="add-label">{{
            t('menuPage.quickAdd.open', { category: category.name })
          }}</span>
        </button>
      </template>
    </template>
  </article>
</template>

<style scoped lang="scss">
  .v2-catalog-category-card {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;

    .card-note {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-4 tw-py-2.5 tw-text-sm tw-text-neutral-600;

      .note-text {
        @apply tw-mr-3 tw-min-w-0 tw-flex-1;
        flex-basis: 220px;
      }

      .note-action {
        @apply tw-my-1;
      }
    }

    .card-empty {
      @apply tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-4 tw-pt-4 tw-text-sm tw-text-neutral-500;
    }

    .card-add {
      @apply tw-flex tw-min-h-[48px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-b-xl tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-bg-transparent tw-px-4 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-600;

      &:hover {
        @apply tw-bg-neutral-50 tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      .add-icon {
        @apply tw-mr-2 tw-text-lg tw-leading-none;
      }

      .add-label {
        @apply tw-min-w-0 tw-truncate;
      }
    }
  }
</style>
